<template>
  <div>
    <v-card class="mx-auto my-4" max-width="100%" flat>

      <v-alert v-if="!session" text close-icon="mt-6 mb-4" color="error">
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-information</v-icon>
          Welcome, please select a session to confirm your clearance eligibility
        </div>
      </v-alert>

      <div v-else>
        <!-- Clearance picker: shown when one or more clearances exist but none is selected -->
        <div v-if="clearances.length > 0 && !clearanceObject">
          <h4 class="mb-3 mt-4">Select a clearance to continue</h4>
          <v-row>
            <v-col v-for="item in clearances" :key="item.id" cols="12" sm="6" md="4">
              <v-card outlined hover class="pa-4 clearance-card h-100" @click="selectClearance(item)">
                <div class="d-flex justify-space-between align-start mb-2">
                  <h4 class="text-truncate mr-2">{{ item.clearance?.name || 'Clearance' }}</h4>
                  <v-chip x-small :color="clearanceTypeColor(item.clearance?.clearanceType)" text-color="white"
                    v-if="item.clearance?.clearanceType">
                    {{ item.clearance.clearanceType }}
                  </v-chip>
                </div>
                <div class="caption mb-1 d-flex align-center">
                  <v-icon x-small class="mr-1" :color="clearanceStatusColor(item.clearanceStatus)">mdi-progress-clock</v-icon>
                  Status: <strong class="ml-1">{{ formatStatus(item.clearanceStatus) }}</strong>
                </div>
                <div class="caption mb-1 d-flex align-center" v-if="item.currentStage">
                  <v-icon x-small class="mr-1">mdi-flag-variant</v-icon>
                  Current stage: <strong class="ml-1 text-truncate">{{ item.currentStage.name }}</strong>
                </div>
                <div class="caption mb-1 d-flex align-center" v-else>
                  <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                  No active stage
                </div>
                <div class="caption mb-3 d-flex align-center" v-if="item.startDateString || item.startDate">
                  <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                  Started: {{ item.startDateString || item.startDate }}
                </div>
                <v-btn small color="primary" depressed block>
                  Open <v-icon small right>mdi-arrow-right</v-icon>
                </v-btn>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <div v-else-if="clearanceObject">
          <v-btn v-if="clearances.length > 1" text small color="primary" class="mb-2 px-0"
            @click="deselectClearance">
            <v-icon small left>mdi-arrow-left</v-icon> All clearances
          </v-btn>
          <v-alert class="mt-6 mb-4" :color="computeAlertStatus" text >
            <div>

            </div>
            <h4 class="ml-8">General Clearance Status</h4>
            <div class="d-flex align-center" v-if="clearanceObject?.clearanceStatus === 'COMPLETED'">
              <v-icon color="primary" class="mr-2">mdi-check</v-icon>
              Congratulations, your Clearance Application was<span class="ml-2" style="font-weight: bold">SUCCESSFUL.</span>
            </div>

            <div class="d-flex align-center" v-else-if="clearanceObject?.clearanceStatus === 'IN_PROGRESS'">
              <v-icon color="warning" class="mr-2">mdi-cancel</v-icon>
              <div>
                Some of the submitted requirements are still pending approval. Please complete any remaining items if necessary.
              </div>

            </div>

            <!-- WHEN ITS REJECTED -->
            <div class="d-flex align-center" v-else>
              <div class="d-flex align-center">
                <v-icon color="error">mdi-close</v-icon>
                <div>Your are currently not eligible for the clearance process, Please check another time.</div>
              </div>

              <!-- <div class="d-flex align-center" v-if="stageStatuses[computeStepper - 1]?.status === 'PENDING'">
                <v-icon class="mr-2">mdi-information</v-icon>
                Complete and submit each requirement individually. All items must be approved to proceed.
              </div>

              <div class="d-flex align-center" v-if="stageStatuses[computeStepper - 1]?.status === 'REJECTED'">
                <v-icon class="mr-2">mdi-danger</v-icon>
                {{ stageStatuses[computeStepper - 1]?.reason }}.
              </div>

              <div class="d-flex align-center" v-else>
                <v-icon class="mr-2">mdi-check-circle</v-icon>
                You are currently not eligible for the clearance process, please check another time.
              </div> -->
            </div>
          </v-alert>
          <template v-if="clearanceObject">
            <v-alert v-if="!clearanceObject.currentStage" text close-icon="mt-6 mb-4" color="error">
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-information</v-icon>
                There is no stage active at the moment. Please Revisit the site later
              </div>
            </v-alert>

            <div v-else>
              <v-stepper v-model="computeStepper" class="elevation-0" alt-labels v-if="stages.length">
                <!-- Stepper Header -->
                <div class="stage-track-wrapper" v-show="computeStages.length > 0">
                  <div class="stage-track">
                    <div
                        v-for="(item, index) in computeStages"
                        :key="index"
                        class="stage-pill"
                        :class="{
                          'stage-pill--active': computeStepper === index + 1 && !isLocked(index),
                          'stage-pill--complete': isStageComplete(index),
                          'stage-pill--rejected': stageStatuses[index]?.status === 'REJECTED',
                          'stage-pill--pending': stageStatuses[index]?.status === 'PENDING' && stageStatuses[index]?.requirementCheck,
                          'stage-pill--locked': isLocked(index)
                        }"
                    >
                      <div class="stage-pill__icon">
                        <v-icon v-if="isStageComplete(index)" x-small color="white">mdi-check</v-icon>
                        <v-icon v-else-if="stageStatuses[index]?.status === 'REJECTED'" x-small color="white">mdi-close</v-icon>
                        <v-icon v-else-if="isLocked(index)" x-small color="white">mdi-lock</v-icon>
                        <span v-else class="stage-pill__number">{{ index + 1 }}</span>
                      </div>
                      <div class="stage-pill__label">
                        <span class="stage-pill__name" :title="item.name">{{ abbreviateStageName(item.name) }}</span>
                        <span v-if="stageStatuses[index]?.status === 'PENDING' && stageStatuses[index]?.requirementCheck" class="stage-pill__status warning--text">Awaiting</span>
                        <span v-else-if="stageStatuses[index]?.status === 'REJECTED'" class="stage-pill__status error--text">Rejected</span>
                      </div>
                    </div>
                  </div>
                </div>


                <!-- Stepper content -->
                <v-stepper-items>
                  <v-alert
                      v-if="!isLocked(computeStepper - 1)"
                      class="mt-4"
                      text
                      :type="computeStageStatus(computeStepper - 1)?.status"
                      :color="computeStageStatus(computeStepper - 1)?.status"
                  >
                    <h4>Stage Status: {{ stageStatuses[computeStepper - 1]?.status }}</h4>
                    <div class="pl-0 ml-0">
                      <div class="d-flex align-center justify-start pl-0 ml-0" v-if="stageStatuses[computeStepper - 1]?.status === 'PENDING' && stageStatuses[computeStepper - 1]?.requirementCheck && stageStatuses[computeStepper - 1]?.allowProgressWithoutApproval">
                        All requirements submitted. Awaiting approval. You may proceed to the next stage.
                      </div>

                      <div class="d-flex align-center justify-start pl-0 ml-0" v-else-if="stageStatuses[computeStepper - 1]?.status === 'PENDING' && stageStatuses[computeStepper - 1]?.requirementCheck">
                        All requirements submitted. Awaiting approval.
                      </div>

                      <div class="d-flex align-center justify-start pl-0 ml-0" v-else-if="stageStatuses[computeStepper - 1]?.status === 'PENDING'">
                        Complete and submit each requirement individually. All items must be approved to proceed.
                      </div>

                      <div class="d-flex align-center justify-start pl-0 ml-0" v-else-if="stageStatuses[computeStepper - 1]?.status === 'REJECTED'">
                        <!-- <v-icon class="mr-2">mdi-danger</v-icon> -->
                        {{ stageStatuses[computeStepper - 1]?.reason }}.
                      </div>

                      <div class="d-flex align-center justify-start pl-0 ml-0" v-else>
                        <!-- <v-icon class="mr-2">mdi-check-circle</v-icon> -->
                        You are currently not eligible for the clearance proces, please check another time.
                      </div>
                    </div>

                  </v-alert>
                  <div v-for="(iter, index) in computeStages" :key="'content' + index">
                    <v-stepper-content :step="index + 1" class="px-6">
                      <div v-if="isLocked(index)" class="text-center my-8">
                        <v-icon large color="grey">mdi-lock</v-icon>
                        <p class="mt-2 grey--text">This stage is not yet accessible. Complete the previous stage first.</p>
                      </div>
                      <div v-else class="mb-6">
                        <!-- {{ stageStatuses[index]}} -->
                        <v-row>
                          <v-col v-for="(item, kth) in (stageMainData[index] || [])" :key="`field-${kth}`" cols="12" sm="6" lg="4">
                            <v-sheet class="pa-4 ma-2 rounded" elevation="1">
                              <!-- Field Header -->
                              <div class="d-flex justify-space-between align-center mb-4">
                                <div class="d-flex align-center">
                                  <v-icon :color="getFieldIconColor(item)" small class="mr-2">
                                    {{ getFieldIcon(item.requirement.requirementType) }}
                                  </v-icon>

                                  <span class="subtitle-1">{{ item.requirement.name }}</span>
                                </div>
                              </div>
                              <!-- Text Input Field -->
                              <div v-if="item.requirement.requirementType === 'TEXT'">
                                <v-text-field v-model="selectedText[index][kth]" :value="item.response?.value" flat solo
                                              dense hide-details="auto" background-color="white"
                                              :placeholder="'Enter ' + item.requirement.name.toLowerCase()"
                                              :disabled="item.response?.value?.length > 0 && item.response?.value === selectedText[index][kth]"
                                              class="mb-3" />
                                <div class="d-flex align-center justify-space-between">
                                  <v-btn small depressed :color="getActionButtonColor()" :loading="loading"
                                         :disabled="!selectedText[index][kth] || item.response?.value === selectedText[index][kth]"
                                         @click="submitField(item, index, kth)">
                                    {{ getActionButtonText() }}
                                  </v-btn>

                                  <v-btn small v-if="stageStatuses[index]?.status !== 'APPROVED'" text depressed
                                         color="primary" @click="editField(index, kth)">
                                    Edit
                                  </v-btn>
                                </div>
                              </div>

                              <!-- Evaluation -->
                              <div v-if="item.requirement.requirementType === 'EVALUATION'">
                                <div class="d-flex align-center justify-space-between">
                                  <!--                              <v-btn small depressed :color="getActionButtonColor()" :loading="loading"-->
                                  <!--                                     :disabled="!evaluations[index][kth]"-->
                                  <!--                                     @click="submitField(item, index, kth)">-->
                                  <!--                                {{ getActionButtonText() }}-->
                                  <!--                              </v-btn>-->

                                  <v-btn small v-if="stageStatuses[index]?.status !== 'APPROVED'" depressed :disabled="!evaluations[index][kth]"
                                         color="primary" @click="initializeEvaluation(item.requirement.id, index, kth)">
                                    {{ evaluations[index][kth] ? 'Resume Evaluation' : '' }}
                                  </v-btn>
                                </div>
                              </div>

                              <!-- File Upload Field -->
                              <div v-else-if="item.requirement.requirementType === 'FILE_UPLOAD'">
                                <v-file-input v-model="selectedFileUpload[index][kth]" flat solo dense hide-details="auto"
                                              background-color="white" show-size truncate-length="15"
                                              :placeholder="item.response?.filePath?.substring(item.response?.filePath?.indexOf('_') + 1) ?? 'Upload ' + item.requirement.name.toLowerCase()"
                                              class="mb-3" :disabled="stageStatuses[index]?.status === 'APPROVED'"
                                              @change="handleFileChange($event, item.requirement.code)">
                                  <template v-slot:selection="{ text }">
                                    <v-chip small label :color="getStatusColor()" text-color="white">
                                      {{ text }}
                                    </v-chip>
                                  </template>
                                </v-file-input>
                                <div class="d-flex align-center justify-space-between">
                                  <v-btn small depressed :color="getActionButtonColor()" :loading="loading"
                                         :disabled="!selectedFileUpload[index][kth] || stageStatuses[index]?.status === 'APPROVED'" @click="submitField(item, index, kth)">
                                    {{ getActionButtonText() }}
                                  </v-btn>

                                  <v-btn small @click="downloadUploadedDocument(item.response.id, item.response?.filePath?.substring(item.response?.filePath?.indexOf('_') + 1) ?? 'Upload ' + item.requirement.name.toLowerCase())" v-if="!!item.response?.filePath" text depressed>
                                    <v-icon small color="primary">mdi-download</v-icon>
                                  </v-btn>
                                </div>
                              </div>

                              <!-- Payment Field -->
                              <div v-else-if="item.requirement.requirementType === 'PAYMENT'">
                                <div class="mb-3">
                                  <div class="subtitle-2 grey--text text--darken-1 mb-1">
                                <span :class="{'success--text': selectedPayments[index][kth]?.cleared, 'error--text': !selectedPayments[index][kth]?.cleared}">
                                  {{ formatCurrency(item.requirement.amount) }}
                                </span>
                                  </div>
                                  <!-- <div class="text-h6">{{ formatCurrency(item.amount) }}</div> -->
                                  <div class="mt-2">
                                    <v-btn small text color="primary" v-if="!selectedPayments[index][kth]" class="mr-2"
                                           @click="generateInvoice(index, kth, item)">
                                      <v-icon left small>mdi-file-document-outline</v-icon>
                                      Initiate
                                    </v-btn>
                                    <v-btn small depressed :color="getActionButtonColor()"
                                           :loading="submitting[item.requirement.code]"
                                           :disabled="!selectedPayments[index][kth] || selectedPayments[index][kth]?.cleared"
                                           @click="loadPaymentGateway(selectedPayments[index][kth], item.requirement)">
                                      <v-icon left small>mdi-credit-card</v-icon>
                                      Pay
                                    </v-btn>
                                    <v-btn small depressed color="info" class="mr-2"
                                           v-if="selectedPayments[index][kth] && !selectedPayments[index][kth]?.cleared"
                                           :loading="loading"
                                           @click="verifyPayment(selectedPayments[index][kth])">
                                      <v-icon left small>mdi-check-decagram</v-icon>
                                      Verify
                                    </v-btn>
                                    <v-btn small text color="primary" v-if="selectedPayments[index][kth]" class="mr-2"
                                           @click="downloadInvoice(selectedPayments[index][kth])">
                                      <v-icon left small>mdi-download</v-icon>
                                      Invoice
                                    </v-btn>
                                  </div>
                                </div>
                              </div>

                              <!-- Validation Field -->
                              <div v-else-if="item.requirement.requirementType === 'VALIDATION'">
                                <v-list dense class="px-0 validation-list rounded">
                                  <v-list-item v-for="(check, checkIdx) in item.requirement.validationTypes" :key="checkIdx"
                                               class=" rounded-lg mb-1 px-0">
                                    <v-list-item-icon class="px-0 d-flex mr-1">
                                      <v-icon small :color="selectedValidation[index] ? 'success' : 'grey'">
                                        {{ selectedValidation[index] ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                      </v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                      <v-list-item-title class="caption">
                                        {{ check }}
                                      </v-list-item-title>
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </div>
                            </v-sheet>
                          </v-col>
                        </v-row>
                      </div>

                      <div class="d-flex justify-space-between justify-md-end py-4" v-if="documents.length > 0 && stageStatuses[computeStepper - 1]?.status === 'APPROVED'">
                        <v-menu offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                color="primary"
                                dark
                                v-bind="attrs"
                                v-on="on"
                                depressed
                            >
                              Stage Documents
                            </v-btn>
                          </template>

                          <v-list>
                            <v-list-item
                                v-for="(item, index) in documents"
                                :key="index + 'poststage-docs'"
                                link
                                @click="downloadPostStageDocument(item)"
                            >
                              <v-list-item-title>{{ item.documentName.substring(0, item.documentName.includes('.') ? item.documentName.lastIndexOf('.') : item.documentName.length) }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>

                      <div class="d-flex justify-space-between py-4">
                        <v-btn text color="grey darken-1 mr-auto" v-if="computeStepper > 1" :disabled="computeStepper === 1" @click="stepperModel--">
                          <v-icon left>mdi-arrow-left</v-icon>
                          Previous
                        </v-btn>
                        <v-btn class="ml-auto" color="primary" text :disabled="!canProceedToNext(computeStepper - 1)" @click="nextStep">
                          {{( computeStepper === stages.length && stageStatuses[computeStepper - 1]?.status !== 'APPROVED') ? 'Complete Clearance' : 'Next Stage' }}
                          <v-icon right>
                          </v-icon>
                        </v-btn>
                      </div>
                    </v-stepper-content>
                  </div>
                </v-stepper-items>
              </v-stepper>
            </div>

          </template>
        </div>
        <div v-else>
          <v-alert text close-icon="mt-6 mb-4" color="error">
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-information</v-icon>
              Clearance currently not available, please check back later.
            </div>
          </v-alert>
        </div>
      </div>
    </v-card>

    <loading-dialog :show="loading"></loading-dialog>
    <message-box :show="messageShow" :message="message" :type="messageType"
      v-on:close-message="messageShow = !messageShow"></message-box>
  </div>
</template>

<script>
import LoadingDialog from "@ui/components/LoadingDialog.vue";
import MessageBox from "@ui/components/MessageBox.vue";
import { formatCurrency } from 'shared/CurrencyUtil'
import ErrorResolver from 'shared/ErrorResolver';
import { mapGetters } from 'vuex';
import Api from "@/main/api";
import GateWays from "shared/GateWays";
import Links from "shared/constants/Links";
import { PaymentGateway } from "shared/PaymentGateway";

export default {
  name: "StudentClearance",
  components: {
    LoadingDialog,
    MessageBox,
  },
  props: ['session'],
  data: () => ({
    message: '',
    messageShow: false,
    messageType: '',
    loading: false,
    submitting: {},
    formData: {},
    clearanceObject: null,
    clearances: [],
    selectedClearance: null,
    submissionStatus: {},
    fieldMessages: {},
    stepperModel: 1,
    stageMainData: [[]],
    stages: [],
    requirementRecord: [],
    selectedRequirement: null,
    clearanceStage: 1,
    clearanceStagePageSize: 0,
    clearanceStageItemPerPage: 10,
    clearancePage: 1,
    clearancePageSize: 0,
    itemPerPage: 10,
    totalPages: 0,
    selectedText: [[]],
    evaluations: [[]],
    selectedFileUpload: [[]],
    selectedStageItem: null,
    selectedPayment: null,
    selectedValidation: [[]],
    selectedPayments: [[]],
    stageStatuses: {},
    stageAccessible: {},
    stateStatus: {},
    paymentConfig: null,
    paymentGateway: null,
    documents: [],
  }),

  computed: {
    ...mapGetters(['getUser']),
    // eslint-disable-next-line vue/return-in-computed-property
    computeAlertStatus: function () {
      switch (this.clearanceObject?.clearanceStatus) {
        case 'IN_PROGRESS':
          return 'warning'

        case 'COMPLETED':
          return 'primary';

        case 'NOT_COMPLETED':
          return 'error';

        default:
          return
      }
    },

    computeStepper() {
      return this.stepperModel;
    },

    computeStages() {
      return this.stages || [];
    }
  },

  methods: {
    formatCurrency,

    navigateToStage(index) {
      if (this.stageAccessible[index] !== true) return;
      this.stepperModel = index + 1;
    },

    isLocked(index) {
      return this.stageAccessible[index] !== true;
    },

    abbreviateStageName(name) {
      if (!name) return '';
      const abbrevMap = {
        'Clearance Form': 'Clr. Form',
        'Biodata form': 'Biodata',
        'Faculty Officer': 'Fac. Officer',
        'Health Services': 'Health Svc.',
        'Hall of Residence': 'Hall of Res.',
        'Security Division': 'Security',
        'Student Affairs': 'Std. Affairs',
        'Bursar/Students Accounts Officer': 'Bursar/SAO',
        'Registrar/Exams & Records Officer': 'Reg./E&R',
      };
      if (abbrevMap[name]) return abbrevMap[name];
      if (name.length > 14) {
        return name.split(/[\s/]+/).map(w => w.length > 4 ? w.substring(0, 4) + '.' : w).join(' ');
      }
      return name;
    },

    getFieldBackgroundColor() {
      if (this.isFieldApproved()) return 'grey lighten-5'
      if (this.isFieldRejected()) return 'grey lighten-5'
      if (this.isFieldPending()) return 'grey lighten-5'
      return 'grey lighten-5'
    },

    getFieldIconColor() {
      if (this.isFieldApproved()) return 'primary'
      if (this.isFieldRejected()) return 'error'
      if (this.isFieldPending()) return 'warning'
      return 'primary'
    },

    getStatusColor() {
      const status = this.clearanceObject?.clearanceStatus;
      switch (status) {
        case 'COMPLETED': return 'success'
        case 'IN_COMPLETE': return 'error'
        case 'IN_PROGRESS': return 'warning';
        default: return 'grey';
      }
    },

    getActionButtonColor() {
      if (this.isFieldApproved()) return 'success'
      if (this.isFieldRejected()) return 'error'
      return 'primary'
    },

    getActionButtonText() {
      if (this.isFieldApproved()) return 'Approved'
      if (this.isFieldRejected()) return 'Rejected'
      if (this.isFieldPending()) return 'Pending'
      return 'Submit'
    },

    getMessageClass() {
      if (this.isFieldApproved()) return 'success--text'
      if (this.isFieldRejected()) return 'error--text'
      if (this.isFieldPending()) return 'warning--text'
      return ''
    },

    /**
     *
     * @param index selected index
     */
    computeStageStatus(index) {
      switch(this.stageStatuses[index]?.status){
        case 'PENDING':
          if (this.stageStatuses[index]?.requirementCheck) {
            return {
              status: 'warning',
              reason: this.stageStatuses[index]?.reason
            };
          }
          return {
            status: 'info',
            reason: this.stageStatuses[index]?.reason
          };

        case 'APPROVED':
          return {
            status: 'success',
            reason: this.stageStatuses[index]?.reason
          };

        case 'REJECTED':
          return {
            status: 'error',
            reason: this.stageStatuses[index]?.reason
          }

        default:
          return
      }
    },

    isFieldSubmitted(code) {
      return !!this.submissionStatus[code]
    },

    /**
     * Check for approval. i.e if its complete
     */
    isFieldApproved() {
      return this.clearanceObject?.clearanceStatus === 'COMPLETED';
    },

    isFieldRejected() {
      return this.clearanceObject?.clearanceStatus === 'IN_REJECTED'
    },

    isFieldPending() {
      return this.clearanceObject?.clearanceStatus === 'IN_COMPLETE';
    },

    editField(index, dataIndex) {
      // Clear the submission status and messages
      if (this.selectedText[index] && !!this.selectedText[index][dataIndex]) {
        this.$set(this.selectedText[index], dataIndex, this.selectedText[index][dataIndex] + ' ')

      }
    },

    extractStageStatus(resData) {
     // const stage = resData?.data?.[0]?.requirement?.stage;
      return {
        status: resData?.status,
        reason: resData?.reason,
        requirementCheck: resData?.requirementCheck ?? false,
        allowProgressWithoutApproval: resData?.data?.canProceed ?? false
      };
    },

    async probeStagesAfter(startIndex) {
      for (let i = startIndex; i < this.stages.length; i++) {
        try {
          const res = await Api.clearance.student.requirementData(this.stages[i].id, this.getUser.userId);
          this.$set(this.stageAccessible, i, true);
          this.$set(this.stageStatuses, i, this.extractStageStatus(res.data));
        } catch {
          this.$set(this.stageAccessible, i, false);
          break;
        }
      }
    },

    isStageComplete(index) {
      return this.stageStatuses[index]?.status === 'APPROVED';
    },

    canProceedToNext(index) {
      const status = this.stageStatuses[index];
      if (!status) return false;
      if (status.status === 'APPROVED') return true;
      if (status.allowProgressWithoutApproval && status.requirementCheck) return true;
      return false;
    },

    getStepperColor(index) {
      if (this.isLocked(index)) return 'grey';
      const status = this.stageStatuses[index];
      if (!status) return 'grey';
      if (status.status === 'APPROVED') return 'success';
      if (status.status === 'REJECTED') return 'error';
      if (status.status === 'PENDING' && status.requirementCheck) return 'warning';
      return 'primary';
    },

    async submitField(item, index, dataIndex) {
      this.submitRequirement(
        this.selectedFileUpload[index] && this.selectedFileUpload[index][dataIndex] ? this.selectedFileUpload[index][dataIndex] : null,
        this.selectedText[index] && this.selectedText[index][dataIndex] ? this.selectedText[index][dataIndex] : null,
        item.requirement?.id,
        this.getUser?.id
      );
    },

    /**
     * Load stages associated to the student
     * @param clearance
     */
    async loadStage(clearance) {
      this.stages = [];

      this.loading = true
      Api.clearance.stages.upaginated(clearance)
        .then(res => {
          this.stages = res.data;
          if (this.stages.length > 0) {
            const currentStageIndex = this.stages.findIndex(s => s.id === this.clearanceObject?.currentStage?.id);
            for (let i = 0; i <= Math.max(currentStageIndex, 0); i++) {
              this.$set(this.stageAccessible, i, true);
            }
            this.probeStagesAfter(Math.max(currentStageIndex, 0) + 1);
            this.loadStageData(0)
            this.fetchPostStageDocument(0)
          }
        }).catch(err => ErrorResolver.process(this, err))
        .finally(() => this.loading = false)
    },

    /**
     * Load stage data associated to the student
     * @param index
     */
    async loadStageData(index) {
      // Ensure all arrays have enough sub-arrays for the requested index
      while (this.stageMainData.length <= index) {
        this.stageMainData.push([]);
      }
      while (this.selectedText.length <= index) {
        this.selectedText.push([]);
      }
      while (this.selectedValidation.length <= index) {
        this.selectedValidation.push([]);
      }
      while (this.selectedFileUpload.length <= index) {
        this.selectedFileUpload.push([]);
      }
      while (this.selectedPayments.length <= index) {
        this.selectedPayments.push([]);
      }
      while (this.evaluations.length <= index) {
        this.evaluations.push([]);
      }

      if (!this.getUser || !this.stages[index])
        return

      this.loading = true
      Api.clearance.student.requirementData(this.stages[index].id, this.getUser.userId)
        .then(res => {
          this.$set(this.stageAccessible, index, true);
          this.$set(this.stageMainData, index, res.data.data);
          this.$set(this.stageStatuses, index, {
            status: res.data?.status,
            reason: res.data?.reason,
            requirementCheck: res.data?.requirementCheck ?? false,
            allowProgressWithoutApproval: res.data?.canProceed ?? res.data?.data?.[0]?.requirement?.stage?.allowProgressWithoutApproval ?? false
          })
          if (res.data.data.length > 0) {
            this.stageMainData[index].forEach((val, idx) => {
              this.assertResponseType(val.requirement?.requirementType, val?.response, index, idx)
            });
          }
        }).catch(err => {
          if (err.response?.status === 404) {
            this.$set(this.stageAccessible, index, false);
          } else {
            ErrorResolver.process(this, err);
          }
        })
        .finally(() => this.loading = false)
    },

    /**
     * Handle file upload
     * @param file Selected file for upload
     * @param code requirement code
     */
    async handleFileChange(file) {
      if (!file) {
        return
      }

      // Add file validation here if needed
      const maxSize = 150 * 1024 // 150KB
      if (file.size > maxSize) {
        this.messageShow = true;
        this.messageType = 'warning';
        this.message = 'File size must be less than 150KB'
        return
      }
    },

    /**
     * Generate payment invoice
     * @param item
     */
    async generateInvoice(index, dataIndex, item) {
      this.initializeTransaction(item.requirement.id, null, index, dataIndex);
        //this.loadPaymentGateway(this.selectedPayments[index] && this.selectedPayments[index][dataIndex] ? this.selectedPayments[index][dataIndex] : null, item.requirement)
    },

    /**
     * Submit payment to requirement
     * @param item
     * @param index payment index
     */
    async submitPayment(item, index) {
      this.$set(this.submitting, item.code, true)
      try {
        // Simulate payment processing
        await new Promise(resolve => setTimeout(resolve, 2000))

        // Simulate successful payment
        this.$set(this.submissionStatus, item.code, 'APPROVED')
        this.$set(this.fieldMessages, item.code, `Payment of ${formatCurrency(item.amount)} processed successfully`)
      } catch (error) {
        this.$set(this.submissionStatus, item.code, 'REJECTED')
        this.$set(this.fieldMessages, item.code, 'Payment processing failed')
      } finally {
        this.$set(this.submitting, item.code, false)
      }
    },

    /**
     * Move to the next stage, if the last stage if the final stage
     * Submit the clearance
     */
    nextStep() {
      if (this.stepperModel === this.stages.length) {
        this.submitFinalClearance()
      } else {
        this.stepperModel++;
      }
    },

    /**
     * Submission of final clearance
     */
    async submitFinalClearance() {
      this.loading = true
      try {
        // Simulate final submission
        await new Promise(resolve => setTimeout(resolve, 2000))

        // Prepare submission data
        const submissionData = {
          personalInfo: {
            fullName: this.formData.fullName,
            matricNumber: this.formData.matricNumber,
            idCard: this.formData.idCard
          },
          academic: {
            courseForm: this.formData.courseForm,
            requirements: this.validationChecks
          },
          payments: {
            clearanceFee: this.submissionStatus.clearanceFee,
            libraryFee: this.submissionStatus.libraryFee
          },
          declaration: this.formData.declaration
        }

        // Emit completion event with submission data
        this.$emit('clearance-completed', submissionData)

        // Show success message
        this.$set(this.fieldMessages, 'final', 'Clearance submitted successfully')
      } catch (error) {
        this.$set(this.fieldMessages, 'final', 'Failed to submit clearance')
      } finally {
        this.loading = false
      }
    },

    /**
     * Get stage icon based on the stage. Note, icons are static
     * @param stage selected stage
     * @param stage
     */
    getStageIcon(stage) {
      return '' //this.stagesData[stage - 1].icon
    },

    getFieldIcon(type) {
      const icons = {
        TEXT: "mdi-form-textbox",
        FILE_UPLOAD: "mdi-file-upload",
        PAYMENT: "mdi-currency-ngn",
        VALIDATION: "mdi-check-circle"
      }
      return icons[type] || "mdi-help-circle"
    },

    /**
     * Reset all stage-related state. Used when switching between clearances
     * and when (re)loading clearance data for a new session/user.
     */
    resetStageState() {
      this.stages = [];
      this.stageMainData = [[]];
      this.stageAccessible = {};
      this.stageStatuses = {};
      this.selectedText = [[]];
      this.selectedValidation = [[]];
      this.selectedFileUpload = [[]];
      this.selectedPayments = [[]];
      this.evaluations = [[]];
      this.stepperModel = 1;
      this.documents = [];
    },

    /**
     * Activate a clearance from the picker — loads its stages and
     * initialises the stepper.
     */
    selectClearance(item) {
      this.resetStageState();
      this.clearanceObject = item;
      if (item?.currentStage?.clearance?.id) {
        this.loadStage(item.currentStage.clearance.id);
      }
    },

    /**
     * Return to the clearance picker (when more than one is available).
     */
    deselectClearance() {
      this.resetStageState();
      this.clearanceObject = null;
    },

    clearanceTypeColor(type) {
      switch (type) {
        case 'ENTRY': return 'primary';
        case 'EXIT': return 'success';
        case 'MID': return 'warning';
        default: return 'grey';
      }
    },

    clearanceStatusColor(status) {
      switch (status) {
        case 'COMPLETED': return 'success';
        case 'IN_PROGRESS': return 'warning';
        case 'NOT_COMPLETED': return 'error';
        default: return 'grey';
      }
    },

    formatStatus(status) {
      if (!status) return '—';
      return status.replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    /**
     * Fetch student clearance data based on the selected userId
     * @param selectedClearance
     */
    loadClearanceData() {

      this.clearanceObject = null;
      this.clearances = [];
      this.resetStageState();

      if (!this.getUser || !this.session)
        return

      this.loading = true;
      Api.clearance.student.findForStudent(this.getUser?.userId, this.session?.id)
        .then(res => {
          // API now returns an array of clearances; tolerate single-object too
          // for backwards compatibility.
          const data = res.data;
          this.clearances = Array.isArray(data) ? data : (data ? [data] : []);

          // Auto-select when there is only one clearance to skip the picker.
          if (this.clearances.length === 1) {
            this.selectClearance(this.clearances[0]);
          }
        }).catch(err => ErrorResolver.process(this, err))
        .finally(() => this.loading = false)
    },

    /**
     * Assert response type by providing the response
     * @param type Selected requirement type
     * @param response selected response associated to a response
     * @param stageIndex
     * @param dataIndex
     */
    assertResponseType(type, response, stageIndex, dataIndex) {
      switch (type) {
        case 'TEXT':
          this.$set(this.selectedText[stageIndex], dataIndex, response?.value);
          break;

        case 'EVALUATION':
          this.$set(this.evaluations[stageIndex], dataIndex, response?.evaluation?.id);
          break;

        /**
         *
         * HOW TO ASSERT FILE_UPLOAD NAME TO FIELD
        */
        case 'FILE_UPLOAD':
          this.$set(this.selectedFileUpload[stageIndex], dataIndex, null);
          break;

        case 'VALIDATION':
          this.$set(this.selectedValidation[stageIndex], dataIndex, response?.validated);
          break;

        // PAYMENT ISNT YET READY
        case 'PAYMENT':
          this.$set(this.selectedPayments[stageIndex], dataIndex, response?.payment);
          break;
        default:
          return;
      }
    },

    downloadUploadedDocument(responseId, path){
      this.loading = true;
      Api.clearance.student.downloadUploadedDocument(responseId)
          .then(res => {
            let fileURL = window.URL.createObjectURL(new Blob([res.data], {type: 'application/pdf'}));
            let fileLink = document.createElement('a');

            fileLink.href = fileURL;
            fileLink.setAttribute('download', path);
            document.body.appendChild(fileLink);

            fileLink.click();
            fileLink.remove();
          }).catch(err => ErrorResolver.checkErrors(this, err))
          .finally(() => this.loading = false)
    },


    initializeEvaluation(requirementId, index, dataIndex){
      if(this.evaluations[index][dataIndex]){
        this.gotoEvaluation(this.evaluations[index][dataIndex])
      } else {
        this.loading = true;
        Api.clearance.student.startEvaluation(this.getUser?.id, requirementId)
            .then(res => {
              this.evaluations[this.stageMainData.length - 1].push(res.data);
              window.location.reload();
            }).catch(err => ErrorResolver.checkErrors(this, err))
            .finally(() => this.loading = false)
      }
    },

    gotoEvaluation(id){
      this.$router.push('evaluation/evaluations/' + id)
    },

    /**
     * Submission of clearance requirement
     * @param selectedFile selected clearance file to be uploaded
     * @param selectedText selected clearance text
     * @param requirementId
     * @param clearanceStudentId
     */
    submitRequirement(selectedFile, selectedText, requirementId, clearanceStudentId) {
      this.loading = true;

      // trim out leading and trailing spaces add due to user input or editing
      if(selectedText){
        selectedText = selectedText.trim()
      }

      Api.clearance.student.submitRequirement(selectedFile, selectedText, requirementId, clearanceStudentId)
        .then(() => {
          this.messageShow = true;
          this.messageType = 'success';
          this.message = 'This requirement submission was successful';
          this.loadStageData(this.stepperModel - 1)
          this.probeStagesAfter(this.stepperModel)

        })
        .catch((err) => ErrorResolver.process(this, err))
        .finally(() => this.loading = false)
    },


    downloadPostStageDocument(doc) {
      if(!this.getUser){
        return
      }

      this.loading = true;

      Api.clearance.postStage.downloadStudentStageDoc(this.getUser?.userId, doc?.id)
        .then((res) => {
          let fileURL = window.URL.createObjectURL(new Blob([res.data], {type: `application/pdf`}));
            let fileLink = document.createElement('a');

            fileLink.href = fileURL;
            fileLink.setAttribute('download', `${doc?.documentName}-${this.getUser?.userId.replace(/[^a-zA-Z0-9]/g, '')}.pdf`);
            document.body.appendChild(fileLink);

            fileLink.click();
            fileLink.remove();
            window.URL.revokeObjectURL(fileURL);
        })
        .catch((err) => ErrorResolver.process(this, err))
        .finally(() => this.loading = false)
    },


    fetchPostStageDocument(index) {
      if(!this.stages[index])
        return

      this.documents = [];
      this.loading = true;
      Api.clearance.student.allStageDocuments(this.stages[index]?.id)
        .then((res) => {
          this.documents  = res.data;
        })
        .finally(() => this.loading = false);
    },

    downloadInvoice(payment) {
      this.loading = true;
      Api.payment.downloadClearanceInvoice(payment.id)
          .then(res => {
            let fileURL = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            let fileLink = document.createElement('a');

            fileLink.href = fileURL;
            fileLink.setAttribute('download', 'invoice.pdf');
            document.body.appendChild(fileLink);

            fileLink.click();
            fileLink.remove();
          }).catch(err => ErrorResolver.checkErrors(this, err)).finally(() => this.loading = false)
    },

    verifyPayment(payment) {
      const invoice = payment?.transactionId ?? payment?.invoice
      if (!invoice) return
      this.loading = true
      Api.payment.processInline(invoice)
          .then(() => {
            this.messageShow = true
            this.messageType = 'success'
            this.message = 'Invoice verified'
            this.loadStageData(this.stepperModel - 1)
          }).catch(err => ErrorResolver.process(this, err))
          .finally(() => this.loading = false)
    },

    initializeTransaction(requirementId, payment, index, dataIndex) {
      if (this.getUser != null && requirementId != null) {
        if(payment == null){
          this.loading = true;
          Api.clearance.student.initializeTransaction(this.getUser.userId, requirementId)
              .then(res => {
                this.message = 'Invoice generated'
                this.messageType = 'success'
                this.messageShow = true
                this.downloadInvoice(res.data)
                // set payment
                this.$set(this.selectedPayments[index], dataIndex, res.data)
              }).catch(err => ErrorResolver.checkErrors(this, err))
              .finally(() => this.loading = false)
        } else {
          this.downloadInvoice(payment)
        }
      }
    },

    makePayment(payment) {
      if (payment != null) {
        if (this.paymentConfig.inline) {
          this.paymentGateway.makePayment({
            email: this.getUser.email,
            amount: payment.invoiceAmount,
            currency: 'NGN',
            invoice: payment.transactionId,
            paymentId: payment.id
          }, (data) => {
            // if its an applicant, logout 'em out
            if (this.isApplicant) {
              LoginUtil.resetAccessToken()
              LoginUtil.resetRefreshToken()
              window.location.href = '/login'
            } else {
              window.location.reload()
            }
          }, (invoice) => {
            // process
            // this.loading = true
            this.getPayment()
            Api.payment.processInline(invoice)
                .then(() => {
                  window.location.href = "/student#/other_fees"
                }).catch(err => {
              ErrorResolver.checkErrors(this, err)
              this.loading = false
            }).finally(() => this.loading = false)
          })
        } else {
          // payment should not be done inline
          // open window for payment
          this.loading = true
          window.location.href = payment.paymentLink
        }
      }
    },

    loadPaymentGateway(payment, requirement) {
      this.loading = true
      this.paymentGateway = new PaymentGateway(GateWays.REMITA, this, Links.payments.processInlinePayment)
      Api.paymentPlatforms.find(requirement.paymentPlatform.name)
          .then(res => {
            if (this.paymentGateway != null) {
              // set config
              this.paymentConfig = res.data
              // if inline, initialize
              if (res.data.inline) {
                setTimeout(() => {
                  this.paymentGateway.initializeGateway(res.data, () => {
                    // make payment
                    this.makePayment(payment)
                    this.loading = false
                  })

                }, 1000)
              } else{ // not inline. make direct payment
                this.makePayment(payment)
              }
            } else {
              this.message = 'No payment settings found'
              this.messageType = 'error'
              this.messageShow = true
              this.loading = false
            }
          }).catch(err => {
        ErrorResolver.checkErrors(this, err)
      }).finally(() => this.loading = false)
    },
  },

  created() {
    // if(!(window.location.href.includes('pg.nsuk.edu.ng') || window.location.href.includes('localhost') || window.location.href.includes('test.nsuk.edu.ng'))){
    //   // push to previous page
    //   window.history.back()
    // }else{
    //   this.loadClearanceData();
    // }
    this.loadClearanceData();
  },

  watch: {
    stepperModel: {
      immediate: true,
      handler(newVal) {
        this.loadStageData(newVal - 1);
        this.fetchPostStageDocument(newVal - 1);
      }
    },

    getUser: function (val) {
      if (val != null) {
        this.loadClearanceData();
      }
    },

    session: function(val){
      if(val)
        this.loadClearanceData();
    }

  }
}
</script>

<style lang="scss" scoped>
.gap-2 {
  gap: 8px;
}

.rounded {
  border-radius: 8px;
}

.validation-list {
  background-color: transparent !important;
}

::v-deep {
  .v-text-field.v-text-field--solo .v-input__control {
    min-height: 40px;
  }

  .v-input--is-focused .v-input__slot {
    border: 1px solid var(--v-primary-base) !important;
  }
}

.v-stepper {
  box-shadow: none !important;
}

.stage-track-wrapper {
  overflow-x: auto;
  padding: 12px 8px;
  background: #f5f7fa;
  border-radius: 8px;
  margin-bottom: 4px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
}

.stage-track {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: max-content;
}

.stage-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  background: white;
  border: 1.5px solid #e0e0e0;
  cursor: default;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 0;

  &--active {
    border-color: var(--v-primary-base, #1976d2);
    background: #e3f2fd;
    font-weight: 600;
  }

  &--complete {
    border-color: #4caf50;
    background: #e8f5e9;
  }

  &--rejected {
    border-color: #f44336;
    background: #ffebee;
  }

  &--pending {
    border-color: #ff9800;
    background: #fff8e1;
  }

  &--locked {
    opacity: 0.5;
    cursor: default;
  }
}

.stage-pill__icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #bdbdbd;
  font-size: 11px;
  color: white;
  font-weight: 600;

  .stage-pill--active & { background: var(--v-primary-base, #1976d2); }
  .stage-pill--complete & { background: #4caf50; }
  .stage-pill--rejected & { background: #f44336; }
  .stage-pill--pending & { background: #ff9800; }
}

.stage-pill__number {
  font-size: 11px;
  line-height: 1;
}

.stage-pill__label {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stage-pill__name {
  font-size: 12px;
  line-height: 1.2;
  color: #333;
}

.stage-pill__status {
  font-size: 10px;
  line-height: 1.2;
}

.clearance-card {
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  border: 1px solid #e0e0e0;
}

.clearance-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
  border-color: var(--v-primary-base, #1976d2);
}

.h-100 {
  height: 100%;
}
</style>
